// ---------------------------------------------------------------------------
// 店舗別 達成度ランキング (横バー + ヒートカラー) — 自前 SVG/DOM (2026-06-25)
//   各店を達成率(売上 ÷ 損益分岐点売上)降順に並べ、横バー長=達成率、
//   色=ヒート(≧110%濃緑→100%緑→90%橙→<90%赤、停止店=灰)。
//   右に安全余裕率と前年比(▲)。行クリックで単店ドリル。
// ---------------------------------------------------------------------------

import React from 'react';

export interface StoreRankRow {
  key: string;
  name: string;
  sales: number;
  breakeven: number;
  achievement: number | null;   // 達成率 (小数, 1.03 = 103%)
  safety: number | null;        // 安全余裕率 (小数)
  priorAchievement?: number | null;
  stopped?: boolean;            // データなし/停止店
  marketMissing?: boolean;      // 市場家賃未投入 → 市場版は未確定
}

const GREEN_DK = '#1f8a4c';
const ORANGE = '#c4732a';
const RED = '#dc2626';
const GREY = '#9ca3af';
const SUB = '#8a8580';

const yenShort = (v: number): string => {
  const a = Math.abs(v);
  const sign = v < 0 ? '▲' : '';
  if (a >= 1e8) return `${sign}¥${(a / 1e8).toFixed(a >= 1e9 ? 0 : 2)}億`;
  if (a >= 1e6) return `${sign}¥${(a / 1e6).toFixed(1)}M`;
  if (a >= 1e4) return `${sign}¥${Math.round(a / 1e3)}千`;
  return `${sign}¥${Math.round(a).toLocaleString('ja-JP')}`;
};

/** 達成率 → ヒートカラー。 */
export function heatColor(achievement: number | null, stopped?: boolean): string {
  if (stopped || achievement == null) return GREY;
  if (achievement >= 1.1) return '#0f7a40';
  if (achievement >= 1.0) return '#2da663';
  if (achievement >= 0.9) return '#d68a3c';
  return '#dc2626';
}

export interface StoreRankBarsProps {
  rows: StoreRankRow[];
  showPrior?: boolean;
  market?: boolean;
  onPick?: (key: string) => void;
}

export const StoreRankBars: React.FC<StoreRankBarsProps> = ({ rows, showPrior, market, onPick }) => {
  const sorted = [...rows].sort((a, b) => {
    if (a.stopped && !b.stopped) return 1;
    if (!a.stopped && b.stopped) return -1;
    return (b.achievement ?? -1) - (a.achievement ?? -1);
  });
  // バー満タン基準: 最大達成率(>120%)か 120% の大きい方。
  const maxAch = Math.max(1.2, ...sorted.map((r) => r.achievement ?? 0));

  const grid = '180px 96px 96px 70px 86px 1fr';

  return (
    <div style={{ fontVariantNumeric: 'tabular-nums' }}>
      {/* ヘッダ */}
      <div style={{
        display: 'grid', gridTemplateColumns: grid, gap: 8, padding: '6px 10px',
        fontSize: '0.6875rem', fontWeight: 700, color: '#6b6560', letterSpacing: '0.03em',
        borderBottom: '1.5px solid #e5e4e2',
      }}>
        <div>店舗</div>
        <div style={{ textAlign: 'right' }}>売上</div>
        <div style={{ textAlign: 'right' }}>分岐点{market ? '(市場)' : ''}</div>
        <div style={{ textAlign: 'right' }}>達成率</div>
        <div style={{ textAlign: 'right' }}>安全余裕率</div>
        <div>達成度</div>
      </div>
      {sorted.map((r) => {
        const color = heatColor(r.achievement, r.stopped);
        const barPct = r.achievement != null ? Math.min(100, (r.achievement / maxAch) * 100) : 0;
        const bePct = Math.min(100, (1 / maxAch) * 100); // 100% (=分岐点) 位置
        const achStr = r.achievement == null ? '—' : `${(r.achievement * 100).toFixed(0)}%`;
        const safeStr = r.safety == null ? '—' : `${r.safety >= 0 ? '' : '▲'}${(Math.abs(r.safety) * 100).toFixed(1)}%`;
        const safeColor = r.safety == null ? SUB : r.safety >= 0 ? GREEN_DK : RED;
        const achUp = showPrior && r.priorAchievement != null && r.achievement != null
          ? r.achievement - r.priorAchievement : null;
        return (
          <div key={r.key}
            onClick={() => onPick && !r.stopped && onPick(r.key)}
            style={{
              display: 'grid', gridTemplateColumns: grid, gap: 8, padding: '8px 10px',
              alignItems: 'center', fontSize: '0.8125rem',
              borderBottom: '1px solid #f1f0ee',
              cursor: onPick && !r.stopped ? 'pointer' : 'default',
              opacity: r.stopped ? 0.55 : 1,
            }}
            onMouseEnter={(e) => { if (onPick && !r.stopped) (e.currentTarget as HTMLDivElement).style.background = '#faf9f7'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = ''; }}
          >
            <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {r.name}
              {r.marketMissing && market && <span style={{ marginLeft: 5, fontSize: '0.625rem', color: ORANGE }}>未投入</span>}
            </div>
            <div style={{ textAlign: 'right', fontWeight: 600 }}>{r.stopped ? '—' : yenShort(r.sales)}</div>
            <div style={{ textAlign: 'right', color: SUB }}>{r.stopped ? '—' : yenShort(r.breakeven)}</div>
            <div style={{ textAlign: 'right', fontWeight: 700, color }}>
              {achStr}
              {achUp != null && (
                <span style={{ fontSize: '0.625rem', marginLeft: 3, color: achUp >= 0 ? GREEN_DK : RED }}>
                  {achUp >= 0 ? '▲' : '▽'}
                </span>
              )}
            </div>
            <div style={{ textAlign: 'right', fontWeight: 600, color: safeColor }}>{safeStr}</div>
            {/* ヒートバー */}
            <div style={{ position: 'relative', height: 18, background: '#efede9', borderRadius: 5, overflow: 'hidden' }}>
              <div style={{
                position: 'absolute', left: 0, top: 0, bottom: 0, width: `${barPct}%`,
                background: `linear-gradient(90deg, ${color}cc, ${color})`, borderRadius: 5,
                transition: 'width .3s ease',
              }} />
              {/* 分岐点(100%)ライン */}
              <div style={{
                position: 'absolute', left: `${bePct}%`, top: -1, bottom: -1, width: 0,
                borderLeft: `1.5px dashed ${market ? '#7c3aed' : '#6b7280'}`,
              }} />
            </div>
          </div>
        );
      })}
      {sorted.length === 0 && (
        <div style={{ padding: '24px', textAlign: 'center', color: SUB }}>表示できる店舗がありません。</div>
      )}
    </div>
  );
};
